import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, TextField } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import ContentPage from "components/contentPage/ContentPage";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import { BranchFavouriteContext } from "contexts/BranchFavouriteContext";
import { MaterialContext } from "contexts/MaterialsContext";
import {
  useQueryCateringEvent,
  useQueryCateringEventMenuDetails,
  invalidateCateringEventsCache,
} from "hooks/CateringEvents";
import {
  CateringEventStatus,
  IUpdateCateringEventRequest,
  IConfigureCateringEventMenuRequest,
  IOverrideMaterialsRequest,
  EVENT_TYPES,
} from "types/CateringEvent";
import { IMaterial } from "types/Material";
import { MealType } from "types/MealType";
import { MeasurementUnit, displayNameForUnitOfMeasure, displayQuantity, IDisplayQuantity } from "types/MeasurementUnit";
import {
  updateCateringEvent,
  configureEventMenu,
  overrideMaterials,
} from "api/CateringApi";
import Toast, { IToastBasicProps } from "components/Toast/Toast";
import { format } from "date-fns";
import styles from "./EditCateringEvent.module.css";

const TIME_SLOTS: { value: string; label: string }[] = [];
for (let h = 0; h < 24; h++) {
  for (const m of [0, 30]) {
    const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? "AM" : "PM";
    const label = `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
    TIME_SLOTS.push({ value, label });
  }
}

interface MenuEntry {
  materialId: string;
  materialName: string;
}

interface RawOverride {
  materialId: string;
  materialName: string;
  unit: string;
  displayUnit: MeasurementUnit;
  computedQty: number;
  overrideQty: number;
}

interface MenuItemOverrideState {
  sellableItemId: string;
  sellableItemName: string;
  rawMaterials: RawOverride[];
  semiPreparedMaterials: RawOverride[];
}

const EditCateringEvent: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const history = useHistory();
  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);
  const { branches } = useContext(BranchFavouriteContext);
  const { branchWiseMaterialsMap, fetchingStatus } = useContext(MaterialContext);
  const { status, eventData, isFetching } = useQueryCateringEvent(eventId);
  const { menuDetails, menuDetailsStatus } = useQueryCateringEventMenuDetails(eventId);

  const [loaded, setLoaded] = useState(false);
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [mealType, setMealType] = useState<MealType>(MealType.LUNCH);
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [numberOfPeople, setNumberOfPeople] = useState<number | "">("");
  const [remark, setRemark] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [eventType, setEventType] = useState("");
  const [menuItems, setMenuItems] = useState<MenuEntry[]>([]);
  const [itemBreakdowns, setItemBreakdowns] = useState<MenuItemOverrideState[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [showToast, setShowToast] = useState<IToastBasicProps>({
    open: false,
    message: "",
    type: "error",
  });

  const event = eventData?.cateringEvent;
  const venueName = branches.find((b) => b.id === event?.branchId)?.name ?? "";

  // Populate form from event data (once)
  useEffect(() => {
    if (event && !loaded) {
      setName(event.name);
      setEventDate(format(new Date(event.eventDate), "yyyy-MM-dd"));
      setMealType(event.mealType);
      setCustomerName(event.customerName);
      setCustomerContact(event.customerContactNumber ?? "");
      setCustomerEmail(event.customerEmail ?? "");
      setNumberOfPeople(event.numberOfPeople);
      setRemark(event.remark ?? "");
      setEventTime(event.eventTimePerMeal?.[event.mealType] ?? "");
      setEventType(event.eventType ?? "");
      setLoaded(true);
    }
  }, [event, loaded]);

  // Populate menu items + per-item material breakdowns from menuDetails
  useEffect(() => {
    if (menuDetails && menuDetailsStatus === "success") {
      setMenuItems(
        menuDetails.menuItemBreakdowns.map((b) => ({
          materialId: b.sellableItemId,
          materialName: b.sellableItemName,
        }))
      );
      setItemBreakdowns(
        menuDetails.menuItemBreakdowns.map((breakdown) => ({
          sellableItemId: breakdown.sellableItemId,
          sellableItemName: breakdown.sellableItemName,
          rawMaterials: breakdown.rawMaterials.map((rm) => {
            const override = breakdown.rawMaterialOverrides?.[rm.materialId];
            return {
              materialId: rm.materialId,
              materialName: rm.materialName,
              unit: displayNameForUnitOfMeasure(rm.displayQuantity.unit),
              displayUnit: rm.displayQuantity.unit,
              computedQty: Number(rm.displayQuantity.quantity),
              overrideQty: override ? Number(override.quantity) : Number(rm.displayQuantity.quantity),
            };
          }),
          semiPreparedMaterials: breakdown.semiPreparedMaterials.map((sp) => {
            const override = breakdown.semiPreparedMaterialOverrides?.[sp.materialId];
            return {
              materialId: sp.materialId,
              materialName: sp.materialName,
              unit: displayNameForUnitOfMeasure(sp.displayQuantity.unit),
              displayUnit: sp.displayQuantity.unit,
              computedQty: Number(sp.displayQuantity.quantity),
              overrideQty: override ? Number(override.quantity) : Number(sp.displayQuantity.quantity),
            };
          }),
        }))
      );
    }
  }, [menuDetails, menuDetailsStatus]);

  if (status === "loading" || isFetching || !loaded || fetchingStatus !== "completed") {
    return (
      <ContentPage
        header={<div className={styles.pageName}>Loading...</div>}
        mainContent={<div className={styles.emptyState}>Loading event...</div>}
      />
    );
  }

  if (!event) {
    return (
      <ContentPage
        header={<div className={styles.pageName}>Event Not Found</div>}
        mainContent={
          <div className={styles.emptyState}>
            Event not found.{" "}
            <span className={styles.backLink} onClick={() => history.push("/catering")}>
              Back to events
            </span>
          </div>
        }
      />
    );
  }

  const isEditable =
    event.status === CateringEventStatus.DRAFT ||
    event.status === CateringEventStatus.CREATED;

  if (!isEditable) {
    history.push(`/catering/events/${event.id}`);
    return null;
  }

  // Get sellable materials — try event's branch first, fall back to all branches
  let sellableMaterials: IMaterial[] = [];
  const branchMaterials = branchWiseMaterialsMap.get(event.branchId);
  if (branchMaterials) {
    sellableMaterials = branchMaterials.sellableMaterialsArrForFoodAnalysis;
  } else {
    const seen = new Set<string>();
    for (const ctx of branchWiseMaterialsMap.values()) {
      for (const m of ctx.sellableMaterialsArrForFoodAnalysis) {
        if (!seen.has(m.id)) {
          seen.add(m.id);
          sellableMaterials.push(m);
        }
      }
    }
  }
  const selectedMaterials = menuItems
    .map((mi) => sellableMaterials.find((m) => m.id === mi.materialId))
    .filter((m): m is IMaterial => !!m);

  const updateRawQty = (itemIdx: number, matIdx: number, qty: number) => {
    setItemBreakdowns((prev) => {
      const next = [...prev];
      const item = { ...next[itemIdx] };
      const mats = [...item.rawMaterials];
      mats[matIdx] = { ...mats[matIdx], overrideQty: qty };
      item.rawMaterials = mats;
      next[itemIdx] = item;
      return next;
    });
  };

  const updateSemiPreparedQty = (itemIdx: number, matIdx: number, qty: number) => {
    setItemBreakdowns((prev) => {
      const next = [...prev];
      const item = { ...next[itemIdx] };
      const mats = [...item.semiPreparedMaterials];
      mats[matIdx] = { ...mats[matIdx], overrideQty: qty };
      item.semiPreparedMaterials = mats;
      next[itemIdx] = item;
      return next;
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Event name is required";
    if (!customerName.trim()) errs.customerName = "Customer name is required";
    if (!numberOfPeople || numberOfPeople < 1) errs.numberOfPeople = "Pax must be at least 1";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // 1. Update basic event fields
      const updateRequest: IUpdateCateringEventRequest = {
        name: name.trim(),
        branchId: event.branchId,
        eventDate: new Date(eventDate).getTime(),
        mealType,
        numberOfPeople: numberOfPeople as number,
        customerName: customerName.trim(),
        customerContactNumber: customerContact || undefined,
        customerEmail: customerEmail || undefined,
        remark: remark || undefined,
        eventTimePerMeal: eventTime ? { [mealType]: eventTime } : undefined,
        eventType: eventType || undefined,
      };
      const updated = await updateCateringEvent(event.id, updateRequest);
      if (!updated) {
        setShowToast({ open: true, message: "Failed to update event", type: "error" });
        return;
      }

      // 2. Configure menu if there are items
      if (menuItems.length > 0) {
        const menuRequest: IConfigureCateringEventMenuRequest = {
          sellableItemIds: menuItems.map((m) => m.materialId),
        };
        const menuResult = await configureEventMenu(event.id, menuRequest);
        if (!menuResult) {
          setShowToast({ open: true, message: "Failed to save menu", type: "error" });
          return;
        }
      }

      // 3. Save per-item material overrides if any differ from computed
      const toBaseUnits = (qty: number, displayUnit: MeasurementUnit): number => {
        if (displayUnit === MeasurementUnit.KILOGRAMS) return qty * 1000;
        if (displayUnit === MeasurementUnit.LITRE) return qty * 1000;
        return qty;
      };
      const perItemRawOverrides: Record<string, Record<string, number>> = {};
      const perItemSemiPreparedOverrides: Record<string, Record<string, number>> = {};
      for (const item of itemBreakdowns) {
        const rawMap: Record<string, number> = {};
        for (const entry of item.rawMaterials) {
          if (entry.overrideQty !== entry.computedQty) {
            rawMap[entry.materialId] = toBaseUnits(entry.overrideQty, entry.displayUnit);
          }
        }
        if (Object.keys(rawMap).length > 0) {
          perItemRawOverrides[item.sellableItemId] = rawMap;
        }
        const semiMap: Record<string, number> = {};
        for (const entry of item.semiPreparedMaterials) {
          if (entry.overrideQty !== entry.computedQty) {
            semiMap[entry.materialId] = toBaseUnits(entry.overrideQty, entry.displayUnit);
          }
        }
        if (Object.keys(semiMap).length > 0) {
          perItemSemiPreparedOverrides[item.sellableItemId] = semiMap;
        }
      }
      if (Object.keys(perItemRawOverrides).length > 0 || Object.keys(perItemSemiPreparedOverrides).length > 0) {
        const overrideRequest: IOverrideMaterialsRequest = {};
        if (Object.keys(perItemRawOverrides).length > 0) {
          overrideRequest.perItemRawMaterialQuantityOverrides = perItemRawOverrides;
        }
        if (Object.keys(perItemSemiPreparedOverrides).length > 0) {
          overrideRequest.perItemSemiPreparedMaterialQuantityOverrides = perItemSemiPreparedOverrides;
        }
        await overrideMaterials(event.id, overrideRequest);
      }

      invalidateCateringEventsCache();
      history.push(`/catering/events/${event.id}`);
    } catch {
      setShowToast({ open: true, message: "Failed to update event", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContentPage
      header={
        <>
          <div className={styles.pageName}>Edit Catering Event</div>
          <span
            className={styles.backLink}
            onClick={() => history.push(`/catering/events/${event.id}`)}
          >
            &larr; Back to Event
          </span>
        </>
      }
      mainContent={
        <div className={styles.container}>
          <Toast
            open={showToast.open}
            message={showToast.message}
            type={showToast.type}
            onClose={() => setShowToast({ ...showToast, open: false })}
          />

          {/* Event Details */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Event Details</div>
            <div className={styles.sectionSubtitle}>
              {format(new Date(eventDate), "dd MMM yyyy")}
              {" | "}
              {mealType.charAt(0) + mealType.slice(1).toLowerCase()}
              {venueName && <>{" | "}{venueName}</>}
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formField}>
                <label className={styles.label}>Event Name *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Event Time</label>
                <select
                  className={styles.select}
                  value={eventTime}
                  onChange={(e) => setEventTime(e.target.value)}
                >
                  <option value="">Select time</option>
                  {TIME_SLOTS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Pax *</label>
                <input
                  type="number"
                  className={styles.input}
                  min={1}
                  value={numberOfPeople}
                  onChange={(e) => setNumberOfPeople(e.target.value ? parseInt(e.target.value) : "")}
                />
                {errors.numberOfPeople && <span className={styles.errorText}>{errors.numberOfPeople}</span>}
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Event Type</label>
                <select
                  className={styles.select}
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                >
                  <option value="">Select type</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Customer Name *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
                {errors.customerName && <span className={styles.errorText}>{errors.customerName}</span>}
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Customer Contact</label>
                <input
                  type="text"
                  className={styles.input}
                  value={customerContact}
                  onChange={(e) => setCustomerContact(e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.label}>Customer Email</label>
                <input
                  type="email"
                  className={styles.input}
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
              </div>
              <div className={styles.formFieldFull}>
                <label className={styles.label}>Remarks</label>
                <textarea
                  className={styles.textarea}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Menu Items</div>
            {selectedMaterials.map((m, idx) => (
              <div key={m.id} className={styles.menuItemRow}>
                <span className={styles.menuItemName}>{m.name}</span>
                <button
                  className={styles.removeBtn}
                  onClick={() => setMenuItems((prev) => prev.filter((_, i) => i !== idx))}
                >
                  &times;
                </button>
              </div>
            ))}
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={sellableMaterials}
              value={selectedMaterials}
              getOptionLabel={(option) =>
                `${option.name} (${displayNameForUnitOfMeasure(option.unitOfMeasure)})`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                setMenuItems(value.map((m) => ({ materialId: m.id, materialName: m.name })));
              }}
              classes={{ root: styles.autocompleteRoot }}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search and add items..."
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </div>

          {/* Raw Material Quantities — per menu item */}
          {itemBreakdowns.some((item) => item.rawMaterials.length > 0) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Raw Material Quantities</div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                Computed by system, edit to override.
              </div>
              {itemBreakdowns.map((item, itemIdx) =>
                item.rawMaterials.length > 0 ? (
                  <div key={item.sellableItemId} className={styles.categoryGroup}>
                    <div className={styles.categoryGroupLabel}>{item.sellableItemName}</div>
                    {item.rawMaterials.map((entry, matIdx) => (
                      <div key={entry.materialId} className={styles.estimateRow}>
                        <span className={styles.estimateName}>
                          {entry.materialName}
                          {entry.overrideQty !== entry.computedQty && (
                            <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>
                              (est. {displayQuantity({ quantity: entry.computedQty, unit: entry.displayUnit })})
                            </span>
                          )}
                        </span>
                        <input
                          type="number"
                          className={styles.estimateQtyInput}
                          min={0}
                          step={0.1}
                          value={entry.overrideQty}
                          onChange={(e) => updateRawQty(itemIdx, matIdx, parseFloat(e.target.value) || 0)}
                        />
                        <span className={styles.estimateUnit}>{entry.unit}</span>
                      </div>
                    ))}
                  </div>
                ) : null
              )}
            </div>
          )}

          {/* Semi-Prepared Material Quantities — per menu item */}
          {itemBreakdowns.some((item) => item.semiPreparedMaterials.length > 0) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Semi-Prepared Material Quantities</div>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                Computed by system, edit to override.
              </div>
              {itemBreakdowns.map((item, itemIdx) =>
                item.semiPreparedMaterials.length > 0 ? (
                  <div key={item.sellableItemId} className={styles.categoryGroup}>
                    <div className={styles.categoryGroupLabel}>{item.sellableItemName}</div>
                    {item.semiPreparedMaterials.map((entry, matIdx) => (
                      <div key={entry.materialId} className={styles.estimateRow}>
                        <span className={styles.estimateName}>
                          {entry.materialName}
                          {entry.overrideQty !== entry.computedQty && (
                            <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>
                              (est. {displayQuantity({ quantity: entry.computedQty, unit: entry.displayUnit })})
                            </span>
                          )}
                        </span>
                        <input
                          type="number"
                          className={styles.estimateQtyInput}
                          min={0}
                          step={0.1}
                          value={entry.overrideQty}
                          onChange={(e) => updateSemiPreparedQty(itemIdx, matIdx, parseFloat(e.target.value) || 0)}
                        />
                        <span className={styles.estimateUnit}>{entry.unit}</span>
                      </div>
                    ))}
                  </div>
                ) : null
              )}
            </div>
          )}

          {/* Submit */}
          <div className={styles.submitRow}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              style={{ backgroundColor: primaryColor, color: primaryTextColor }}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => history.push(`/catering/events/${event.id}`)}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default EditCateringEvent;
